/**
 * Prototype Pollution Protection
 * 
 * This script provides comprehensive protection against prototype pollution attacks
 * by implementing safe object operations and input sanitization.
 */

(function() {
    'use strict';

    // Store original Object.prototype to prevent tampering
    const originalObjectPrototype = Object.prototype;
    const originalArrayPrototype = Array.prototype;
    const originalFunctionPrototype = Function.prototype;

    // Dangerous property names that should be blocked
    const DANGEROUS_PROPERTIES = [
        '__proto__',
        'proto',
        'constructor',
        'prototype',
        'constructor.prototype',
        'constructor.prototype.__proto__'
    ];

    /**
     * Check if a property name is dangerous for prototype pollution
     * @param {string} prop - Property name to check
     * @returns {boolean} - True if property is dangerous
     */
    function isDangerousProperty(prop) {
        if (typeof prop !== 'string') return false;
        
        // Check exact matches for known dangerous properties
        if (DANGEROUS_PROPERTIES.includes(prop)) return true;
        
        // Only flag as dangerous if it's clearly a prototype pollution attempt
        // Don't block properties that just happen to contain these words
        if (prop === '__proto__' || 
            prop === 'constructor' || 
            prop === 'prototype' ||
            prop.startsWith('__proto__.') ||
            prop.startsWith('constructor.') ||
            prop.startsWith('prototype.')) {
            return true;
        }
        
        return false;
    }

    /**
     * Safe object merge function that prevents prototype pollution
     * @param {Object} target - Target object
     * @param {Object} source - Source object to merge
     * @param {boolean} deep - Whether to perform deep merge
     * @returns {Object} - Merged object
     */
    function safeMerge(target, source, deep = false) {
        if (!target || typeof target !== 'object') {
            target = {};
        }
        
        if (!source || typeof source !== 'object') {
            return target;
        }

        // Create a safe copy of the target
        const result = Object.create(null);
        
        // Copy existing properties from target
        for (const key in target) {
            if (target.hasOwnProperty(key) && !isDangerousProperty(key)) {
                result[key] = target[key];
            }
        }

        // Merge properties from source
        for (const key in source) {
            if (source.hasOwnProperty(key) && !isDangerousProperty(key)) {
                if (deep && 
                    typeof source[key] === 'object' && 
                    source[key] !== null && 
                    !Array.isArray(source[key])) {
                    result[key] = safeMerge(result[key] || {}, source[key], true);
                } else {
                    result[key] = source[key];
                }
            }
        }

        return result;
    }

    /**
     * Safe property setter that prevents prototype pollution
     * @param {Object} obj - Object to set property on
     * @param {string} prop - Property name
     * @param {*} value - Property value
     * @returns {boolean} - True if property was set successfully
     */
    function safeSetProperty(obj, prop, value) {
        if (!obj || typeof obj !== 'object') return false;
        if (isDangerousProperty(prop)) return false;
        
        try {
            obj[prop] = value;
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Sanitize URL parameters to prevent prototype pollution
     * @param {string} queryString - URL query string
     * @returns {Object} - Sanitized parameters object
     */
    function sanitizeUrlParams(queryString) {
        const params = {};
        
        if (!queryString || typeof queryString !== 'string') {
            return params;
        }

        // Remove leading '?' if present
        const cleanQuery = queryString.replace(/^\?/, '');
        
        if (!cleanQuery) return params;

        const pairs = cleanQuery.split('&');
        
        for (const pair of pairs) {
            const [key, value] = pair.split('=');
            
            if (key && !isDangerousProperty(decodeURIComponent(key))) {
                params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
            }
        }

        return params;
    }

    /**
     * Check if an object is from a trusted source (like Usercentrics cookie banner)
     * @param {Object} obj - Object to check
     * @returns {boolean} - True if object is from trusted source
     */
    function isTrustedObject(obj) {
        if (!obj) return false;
        
        // Check if object is from Usercentrics
        if (window.UC_UI || window.Usercentrics || 
            (obj.constructor && obj.constructor.name && 
             (obj.constructor.name.includes('Usercentrics') || 
              obj.constructor.name.includes('UC_')))) {
            return true;
        }
        
        // Check if object is from cookie banner scripts
        const objString = obj.toString();
        if (objString.includes('usercentrics') || objString.includes('UC_')) {
            return true;
        }
        
        return false;
    }

    /**
     * Override jQuery.extend to add additional protection
     * Only sanitizes actual prototype pollution attempts, not legitimate operations
     */
    if (typeof jQuery !== 'undefined' && jQuery.extend) {
        const originalExtend = jQuery.extend;
        
        jQuery.extend = function() {
            const args = Array.prototype.slice.call(arguments);
            const target = args[0] || {};
            const deep = typeof target === 'boolean' ? target : false;
            const start = deep ? 1 : 0;
            
            // Check if target is a trusted object (like Usercentrics)
            const isTrusted = isTrustedObject(target);
            
            // Enhanced protection for jQuery.extend, especially deep extend
            // Skip sanitization for trusted objects
            if (!isTrusted) {
                for (let i = start; i < args.length; i++) {
                    const source = args[i];
                    if (source && typeof source === 'object' && !isTrustedObject(source)) {
                        // Only remove dangerous properties that are actual pollution attempts
                        for (const key in source) {
                            if (source.hasOwnProperty(key) && 
                                isDangerousProperty(key) &&
                                // Only block if targeting a plain object (not trusted objects)
                                (target === Object.prototype || 
                                 (typeof target === 'object' && target.constructor === Object))) {
                                delete source[key];
                            }
                        }
                        
                        // Additional protection for deep extend with nested objects
                        if (deep) {
                            const sanitizeObject = function(obj, depth) {
                                depth = depth || 0;
                                // Limit recursion depth to avoid performance issues
                                if (depth > 10) return;
                                
                                if (obj && typeof obj === 'object' && !isTrustedObject(obj)) {
                                    for (const prop in obj) {
                                        if (obj.hasOwnProperty(prop)) {
                                            // Only remove if it's clearly a pollution attempt
                                            if (isDangerousProperty(prop) && 
                                                (prop === '__proto__' || prop === 'constructor' || prop === 'prototype')) {
                                                delete obj[prop];
                                            } else if (typeof obj[prop] === 'object' && obj[prop] !== null && !Array.isArray(obj[prop])) {
                                                sanitizeObject(obj[prop], depth + 1);
                                            }
                                        }
                                    }
                                }
                            };
                            sanitizeObject(source);
                        }
                    }
                }
            }
            
            return originalExtend.apply(this, args);
        };
    }

    /**
     * Protect Object.prototype from modification
     * Note: We don't freeze prototypes globally as this can interfere with legitimate scripts
     * Instead, we monitor and block only dangerous property access attempts
     */
    function protectObjectPrototype() {
        // Don't freeze prototypes - this is too aggressive and breaks legitimate scripts
        // Instead, we rely on property access monitoring to block dangerous operations
    }

    /**
     * Monitor and block dangerous property access
     * Only blocks actual prototype pollution attempts, not legitimate property access
     */
    function setupPropertyAccessMonitoring() {
        const originalDefineProperty = Object.defineProperty;
        
        Object.defineProperty = function(obj, prop, descriptor) {
            // Allow trusted objects (like Usercentrics) to define properties freely
            if (isTrustedObject(obj)) {
                return originalDefineProperty.call(this, obj, prop, descriptor);
            }
            
            // Only block if it's a dangerous property AND targeting a prototype
            if (isDangerousProperty(prop) && 
                (obj === Object.prototype || obj === Array.prototype || obj === Function.prototype ||
                 obj === originalObjectPrototype || obj === originalArrayPrototype || obj === originalFunctionPrototype)) {
                return obj;
            }
            
            return originalDefineProperty.call(this, obj, prop, descriptor);
        };

        const originalSetPrototypeOf = Object.setPrototypeOf;
        Object.setPrototypeOf = function(obj, proto) {
            // Allow trusted objects to set prototypes
            if (isTrustedObject(obj) || isTrustedObject(proto)) {
                return originalSetPrototypeOf.call(this, obj, proto);
            }
            
            // Only block if trying to set prototype to a built-in prototype (actual pollution)
            if (proto === Object.prototype || proto === Array.prototype || proto === Function.prototype) {
                // Allow if obj is not a plain object (might be legitimate)
                if (obj && obj.constructor && obj.constructor !== Object && obj.constructor !== Array) {
                    return originalSetPrototypeOf.call(this, obj, proto);
                }
                return obj;
            }
            return originalSetPrototypeOf.call(this, obj, proto);
        };
    }

    /**
     * Initialize protection
     */
    function init() {
        // Protect prototypes
        protectObjectPrototype();
        
        // Setup monitoring
        setupPropertyAccessMonitoring();
        
        // Expose safe functions globally
        window.safeMerge = safeMerge;
        window.safeSetProperty = safeSetProperty;
        window.sanitizeUrlParams = sanitizeUrlParams;
        window.isDangerousProperty = isDangerousProperty;
        
        // Protection initialized
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
